import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { expertService } from '@/services/api'
import type { ExpertProfile, PaginatedResponse } from '@/types'

export default function ExpertsListPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['experts', page, search],
    queryFn: async () => {
      const response = await expertService.list({ page, limit: 20, search: search || undefined })
      return response.data as PaginatedResponse<ExpertProfile>
    },
  })

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Find IT Experts</h1>
        <p className="text-gray-600">Browse verified IT professionals in Ireland</p>
      </div>

      {/* Search and filters */}
      <div className="mb-8 flex gap-4">
        <input
          type="text"
          placeholder="Search experts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-md"
        />
        <select className="input max-w-xs">
          <option value="">All Categories</option>
          <option value="Dev">Development</option>
          <option value="DevOps">DevOps</option>
          <option value="Security">Security</option>
          <option value="Data">Data</option>
          <option value="AI/ML">AI/ML</option>
          <option value="Cloud">Cloud</option>
        </select>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading experts...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-600">Failed to load experts. Please try again.</p>
        </div>
      ) : data?.data?.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No experts found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data?.data?.map((expert) => (
              <Link
                key={expert.id}
                to={`/experts/${expert.id}`}
                className="card hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-xl font-semibold">
                    {expert.first_name?.[0]}{expert.last_name?.[0]}
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {expert.display_name || `${expert.first_name} ${expert.last_name}`}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-1">{expert.headline}</p>
                    <p className="text-sm text-gray-500">{expert.city}, {expert.country}</p>
                  </div>
                  {expert.verified_at && (
                    <span className="badge-success">Verified</span>
                  )}
                </div>
                {expert.hourly_rate_min && (
                  <p className="mt-4 text-sm text-gray-600">
                    €{expert.hourly_rate_min} - €{expert.hourly_rate_max}/hr
                  </p>
                )}
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <div className="mt-8 flex justify-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-gray-600">
                Page {page} of {data.total_pages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
                disabled={page === data.total_pages}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
